import { noop } from '@aloft/toolkit-shared';
import { Block, Flex, Modal } from '@aloft/toolkit-sui';

import { PropsWithChildren, useState } from 'react';

import { PropertyHeader } from '../../../../components/PropertyHeader/PropertyHeader.tsx';
import { propertySectionTiles } from '../../../../components/ReferenceBar/consts.ts';
import { setActiveID as setActiveReferenceBarID } from '../../../../redux/features/referenceBarUISlice.ts';
import { useAppDispatch } from '../../../../redux/hooks.ts';
import { Tkv2ReferenceViewModule } from '../../../referenceView/Tkv2ReferenceViewModule.tsx';
import { DataSheet } from '../../../referenceView/types.ts';
import { getReferenceProperty } from '../../../utils/getReferenceProperty.ts';
import { PropertyTab } from './PropertyTab.tsx';
import { ReferenceTab } from './ReferenceTab.tsx';

import '../../AnalysisModule.scss';

type FormWithReferenceViewModalProps = {
  activeId: string;
  onModalClose: () => void;
  isModalOpen: boolean;
  setActiveId: (id: string) => void;
  compSequence: string[];
  formValid: boolean;
  invalidFormWarning: string;
  includeSubjectArv?: boolean;
};
export const FormWithReferenceViewModal = ({
  activeId,
  isModalOpen,
  onModalClose,
  setActiveId,
  compSequence,
  formValid,
  invalidFormWarning,
  children,
  includeSubjectArv,
}: PropsWithChildren<FormWithReferenceViewModalProps>) => {
  const [activeTab, setActiveTab] = useState('mls-listing');
  const dispatch = useAppDispatch();

  const propertyIds = includeSubjectArv
    ? ['subject', 'subjectArv', ...compSequence]
    : ['subject', ...compSequence];

  return (
    <Modal
      size="6xl"
      isOpen={isModalOpen}
      onClose={formValid ? onModalClose : noop}
      closeOnEsc={formValid}
      showCloseButton={false}
      overflow="hidden"
      primaryAction={{
        id: 'add-btn',
        text: 'Close',
        onClick: onModalClose,
        disabled: !formValid,
        tooltipText: formValid ? undefined : invalidFormWarning,
      }}
      fullHeight
    >
      <Flex
        height="100%"
        width="100%"
        overflow="hidden"
        flexDirection="column"
        // We need the analysis-module class name for scoping here,
        // because the modal is rendered in a chakra portal outside of
        // the document flow and doesn't actually sit inside the analysis module
        className="analysis-module"
      >
        <Flex
          className="property-tabs"
          overflowX="auto"
          flexGrow={1}
          flexShrink={0}
          flexBasis="auto"
        >
          {propertyIds.map((id, idx) => (
            <PropertyTab
              compSequence={compSequence}
              key={id}
              id={id}
              idx={propertyIds.includes('subjectArv') ? idx - 1 : idx}
              active={id === activeId}
              disabled={!formValid}
              onClick={() => {
                setActiveId(id);
                dispatch(setActiveReferenceBarID(getReferenceProperty(id, compSequence).id));
              }}
            />
          ))}
        </Flex>
        <Flex width="100%" overflow="hidden">
          <Flex flexDirection="column" width="27rem" flexShrink={0}>
            <Block>
              <PropertyHeader propertyId={activeId} hideActions />
            </Block>
            {children}
          </Flex>
          <Flex height="100%" backgroundColor="surface2" width="100%">
            <Block height="100%" width="100%" overflow="auto">
              <Block padding="1.75rem" height="100%" width="100%">
                <Tkv2ReferenceViewModule
                  dataSheet={activeTab as DataSheet}
                  isPopout={false}
                  isWrapped
                />
              </Block>
            </Block>
            <Flex flexDirection="column" className="reference-tabs" flexShrink={0}>
              <ReferenceTab
                id={activeId}
                label="Property Map"
                icon="map-2"
                isSelected={activeTab === 'mls-map'}
                onClick={() => setActiveTab('mls-map')}
              />
              <Flex
                height=".125rem"
                width="90%"
                margin="0 auto"
                flexShrink={0}
                backgroundColor="strokeActive"
              />
              {propertySectionTiles.map((item) => (
                <ReferenceTab
                  id={activeId}
                  key={item.label}
                  label={item.label}
                  icon={item.icon}
                  isSelected={item.dataSheet === activeTab}
                  onClick={() => setActiveTab(item.dataSheet)}
                />
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};
